import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, KeyRound, Loader2, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { LOGO_MARK } from '../lib/brand';
import { PlatformPasswordFields, passwordError } from './PlatformPasswordFields';
import './console.css';

export default function PlatformPasswordRecovery({ reset = false }) {
  const { logout } = useAuth();
  // Fragment tokens aren't sent in page URLs, referrers or access logs. Keep only
  // component memory after opening; no localStorage/sessionStorage credentials.
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '');
  const [email, setEmail] = useState('');
  const [form, setForm] = useState({ next: '', confirm: '' });
  const [state, setState] = useState(reset ? 'checking' : 'ready');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (!reset) return undefined;
    window.history.replaceState(window.history.state, '', window.location.pathname);
    let active = true;
    api.post('/auth/platform/reset-password/check', { token }).then(() => {
      if (active) setState('ready');
    }).catch((err) => {
      if (active) { setState('invalid'); setError(token ? passwordError(err, 'Could not check this link. Reopen the email link to try again.') : 'This reset link is invalid or has expired. Request a new link.'); }
    });
    return () => { active = false; };
  }, [reset, token]);
  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (reset && form.next !== form.confirm) return setError('Passwords do not match');
    setBusy(true);
    try {
      if (reset) {
        await api.post('/auth/platform/reset-password', { token, new_password: form.next });
        setForm({ next: '', confirm: '' });
        await logout();
        setMessage('Password updated. Sign in with your new password.');
      } else {
        const { data } = await api.post('/auth/platform/forgot-password', { email: email.trim() });
        setMessage(data.message);
      }
      setState('success');
    } catch (err) {
      setError(passwordError(err, 'Could not complete the request. Please try again.'));
      if (err?.response?.status === 410) setState('invalid');
    } finally { setBusy(false); }
  };
  return <div className="pc-shell pc-login-shell" data-testid="pc-password-recovery">
    <form className="pc-login-card" onSubmit={submit} data-testid="pc-recovery-form">
      <div className="pc-login-brand"><img src={LOGO_MARK} className="pc-login-logo-mark" alt="" aria-hidden="true" />DutiLyne <small>platform console</small></div>
      <h1 className="pc-h1" data-testid="pc-recovery-title">{reset ? 'Reset password' : 'Forgot password?'}</h1>
      {state === 'checking' && <p role="status" data-testid="pc-recovery-checking"><Loader2 className="h-4 w-4 animate-spin" /> Checking your link…</p>}
      {state === 'ready' && <>
        {reset ? <PlatformPasswordFields form={form} setForm={setForm} busy={busy} /> :
          <label className="pc-login-field">Work email<input className="pc-input" type="email" autoComplete="email" required
            maxLength={254} value={email} disabled={busy} onChange={(e) => setEmail(e.target.value)} data-testid="pc-recovery-email" /></label>}
        <button className="pc-btn pc-btn-primary pc-login-submit" disabled={busy} data-testid="pc-recovery-submit">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : reset ? <KeyRound className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
          {busy ? 'Please wait…' : reset ? 'Save new password' : 'Send reset link'}
        </button>
      </>}
      {error && <p role="alert" className="pc-password-error" data-testid="pc-recovery-error">{error}</p>}
      {state === 'success' && <p role="status" className="pc-password-success" data-testid="pc-recovery-success">{message}</p>}
      {state === 'invalid' && <Link className="pc-password-link" to="/platform/forgot-password" data-testid="pc-recovery-new-link">Request a new link</Link>}
      <Link className="pc-btn" to="/platform" data-testid="pc-recovery-back"><ArrowLeft className="h-3.5 w-3.5" /> {reset && state === 'success' ? 'Sign in' : 'Back to console sign-in'}</Link>
    </form>
  </div>;
}